<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { usePriorityBooking } from "@/store/usePriorityBookings";
import { useEventStore } from "@/store/useEventStore";
import { useCalendarStore } from "@/store/useCalendarStore";
import { useUserStore } from "@/utils/UserStore";
import { useUserRole } from "@/utils/useUserRole";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useListStore } from "@/store/listStore";
import { moment } from "@/utils/useTimeZone";
import isEmpty from "lodash/isEmpty";
import QrcodeVue from "qrcode.vue";

// Interface
interface Area {
  location: number;
}

interface Location {
  id: number;
  name: string;
}

// store
const priorityBooking = usePriorityBooking();
const events = useEventStore();
const calendar = useCalendarStore();
const userStore = useUserStore();
const getSetupStore = useGlobalSetupStore();
const userRole = useUserRole();
const list = useListStore();

const { handleEventEdit } = events;
const { deleteBooking, getUserList } = calendar;
const { getListArea, fetchActivities } = list;

const { getPriorityBookings } = priorityBooking;
const { getLocationList } = list;
const { priorityBookings, isLoading, isConfirmEditBookingDialog } =
  storeToRefs(priorityBooking);
const {
  addEditEventDialog,
  eventData,
  recurringDetails: eventRecurringDetails,
  confirmEditDialog,
  selectedEvent,
} = storeToRefs(events);
const {
  usersList,
  recurringDetails,
  isBookingStepperVisible,
  editBookingData,
} = storeToRefs(calendar);
const { currentLocation } = storeToRefs(userStore);
const { globalSetup } = storeToRefs(getSetupStore);
const { permissionListMap, userInfo, rolesList } = storeToRefs(userRole);
const { listArea, activityList, locationList } = storeToRefs(list);

// Data
const recurringConfig = ref<any>("");
const isQrCodeVisible = ref<boolean>(false);
const selectedBookingId = ref<string>("");

// computed
const getMyRole = computed(() => {
  return rolesList?.value?.filter((role: any) =>
    userInfo.value?.roles?.includes(role.id),
  );
});

const getLocation = computed(() => (area: Area) => {
  const locations = locationList.value || [];
  const location = locations.find((loc: Location) => loc.id === area.location);
  return location?.name || "";
});

const userId = computed(() => pb.authStore.model?.id);

const getDay = computed(() => {
  const today = moment().startOf("day");
  const tomorrow = moment(today).add(1, "day");

  return (dateTime: string) => {
    const bookingDate = moment(dateTime).startOf("day");

    if (bookingDate.isSame(today)) {
      return "Today";
    } else if (bookingDate.isSame(tomorrow)) {
      return "Tomorrow";
    }
    return bookingDate.format("dddd, DD-MM");
  };
});

const viewQR = ref(false)
const finalLocks:any = ref([])
const watchPosId:any = ref(null)
const lockLoading = ref(false)
const isAfterLock:any = ref({})
const locationErrorMsq = ref('')

const openQR = async (booking:any) => {
  viewOnlyWindowBooking.value = booking

  // Get users Permissions
  isAfterLock.value = {}
  finalLocks.value = [];

  try {
    if ((pb?.authStore?.model?.membership && pb?.authStore?.model?.membership != '') || (pb?.authStore?.model?.roles && pb?.authStore?.model?.roles.length > 0)) {

      let roles = pb?.authStore?.model?.roles && pb?.authStore?.model?.roles.length > 0 ? pb?.authStore?.model?.roles.map((role:any) => 'roles~"' + role + '"').join("||") : ''
      if (roles != '') {
        roles = '|| ' + roles
      }

      // Get Activity Location
      let location = pb?.authStore?.model?.homeLocation
      try{
        let locationActivityCheck = await pb.collection("activities").getOne(booking.activity)
        if(locationActivityCheck){
          location = locationActivityCheck.location
        }
      }catch(e){}

      let locks = await pb.collection("doors").getFullList(
        {filter: 'enabled=true && location="' + location + '" && enablePhoneGeoAccess = true && ' +
            'bookingActivityAccess~"' + (booking?.activity || '') + '"' +
            '&& (membershipaccess~"' + pb?.authStore?.model?.membership + '" || allMemberships=true' + roles + ')'})

      // remove Locks
      if (locks) {
        let bookingTime = moment(booking.startDate)
        let bookingTimeEnd = moment(bookingTime).add(booking.duration, 'minutes');
        let now = moment()
        locks.forEach(lock => {
          lock.enabled = false
          if(lock?.beforeBookingMins > 0){
            let openTime = moment(bookingTime).add((lock?.beforeBookingMins || 0) * -1,"minutes")
            let closeTime = moment(bookingTimeEnd).add(lock?.afterBookingMins || 0, "minutes")
            if(now.isBetween(openTime, closeTime)){
              finalLocks.value.push(lock)
            } else {
              locationErrorMsq.value = "Must be within booking window to open door"
            }
          } else {
            finalLocks.value.push(lock)
          }
        })
      }
    }
  }catch(e){}

  viewQR.value = true
}

const openViewOnlyWindow = ref(false)
const viewOnlyWindowBooking = ref({});
// Methods
const handleEdit = async (booking: any) => {
  // Navigate to booking edit page
  if (booking.expand?.area?.location && booking.expand.area.location != userStore.currentLocation){
    // Get all Users in Booking
    booking.members = await pb.collection('bookings').getOne(booking.id, {expand:'users, options',
      fields:'expand.users.firstName, expand.users.lastName, expand.users.email, expand.users.id, expand.users.mobileNumber, expand.options.name'})
    viewOnlyWindowBooking.value = booking
    openViewOnlyWindow.value = true
    return
  }
  const {
    recurringBookingDetails,
    eventDetails,
    recurringEventDetails,
    expand,
  } = booking;


  // manage recurring booking dialog
  const baseBookingData = {
    ...booking,
    selectedOptions: booking?.options || [],
    startDate: booking?.startTime,
    docketId: expand?.docketLine?.docket,
  };

  if (!isEmpty(recurringEventDetails)) {
    // Manage recurring event dialog
    const { id, events, frequency, interval, days, endDate, expand } =
      recurringEventDetails;

    const eventData = expand?.events?.find((event: any) =>
      event.expand?.bookings?.some((book: any) => book.id === booking?.id),
    );

    if (eventData) {
      selectedEvent.value = {
        ...eventData,
        recurring: {
          recurringId: id,
          events,
          frequency,
          interval,
          days,
          endDate,
        },
      };
      eventRecurringDetails.value = {
        isEditAll: false,
        recurringId: id,
        events,
      };
      isConfirmEditBookingDialog.value = true; // Show warning dialog
      confirmEditDialog.value = {
        type: "Event",
        title: "Edit Recurring Event",
        alertMessage:
          "This is one event in a series. What would you like to do?",
      };
    }
    return;
  }
  if (!isEmpty(eventDetails)) {
    // Manage event dialog
    confirmEditDialog.value = {
      type: "Event",
      title: "Edit Event",
      alertMessage: "Edit details for this event.",
    };
    handleEventEdit(eventDetails);
    return;
  }
  if (!isEmpty(recurringBookingDetails)) {
    const { interval, days, frequency, endDate } = recurringBookingDetails;
    editBookingData.value = {
      ...baseBookingData,
      recurring: { interval, days, frequency, endDate },
    };
    recurringConfig.value = recurringBookingDetails;
    isConfirmEditBookingDialog.value = true;
    confirmEditDialog.value = {
      type: "Booking",
      title: "Edit Recurring Booking",
      alertMessage:
        "This is one booking in a series. What would you like to do?",
    };
    return;
  }

  // Normal booking edit
  editBookingData.value = baseBookingData;
  isBookingStepperVisible.value = true;
};

const closeDialog = (value: boolean) => {
  isBookingStepperVisible.value = value;
  editBookingData.value = {};
};

const handleBookingEdit = (editType: string) => {
  /**
   * Handles the editing of a booking, recurring booking, or event.
   *
   * @param {string} editType - The type of edit to be performed. If 'ALL', all related bookings will be edited.
   *
   * @returns {void}
   */
  recurringDetails.value = {
    isEditAll: editType && editType === "ALL" ? true : false,
    recurringId: recurringConfig?.value?.id,
    bookings: recurringConfig?.value?.bookings,
  };

  isBookingStepperVisible.value = true;
  isConfirmEditBookingDialog.value = false;
};

watch(currentLocation.value, async (newVal) => {
  if (newVal) {
    await fetchActivities();
  }
});

const FormatDate = (startDate:any) => {
  try {
    return startDate ? moment(startDate).isSame(moment(), "day")
      ? `Today ${moment(startDate).format("h:mm A")}`
      : `${moment(startDate).format("dddd DD-MM-YY h:mm A")}` : "Unknown Start Time"
  } catch(e){
    return "Unknown Start Time"
  }
}

const isInCurrentTimeSlot = (bookingStartTime:any,duration:number) => {

  const bookingStart = moment(bookingStartTime)
  const currentTime = moment();
  const bookingEnd = bookingStart.clone().add(duration, "minutes");

  return currentTime.isBetween(bookingStart, bookingEnd);
}

const isOutHourWarningVisible = (bookingStartTime:any) => {
  let d = new Date() // Get Now
  let HourBefore = new Date(bookingStartTime)
  HourBefore.setHours(HourBefore.getHours() - 1)
  let BookingStart = new Date(bookingStartTime); // The Booking Start Time
  // Has the Time Passed?
  if(d >= BookingStart){
    return false
  }
  if(HourBefore >= BookingStart){
    return false
  }
}

const openDoor = async (lock:any) => {
  lockLoading.value = true
  if(!watchPosId.value && lock?.id) {
    isAfterLock.value = lock
    getLocationDoor()
    lockLoading.value = false
    return
  } else if(lock?.id && lock?.enabled) {
    let lockState = await pb.collection("doors").update(lock.id, {activate: true})
    lock.state = "Open"
    lock.activate = lockState.activate
    if (lockState?.onSeconds) {
      window.setTimeout(async () => {
        let tmpLock = await pb.collection("doors").getOne(lock.id)
        tmpLock.enabled = lock.enabled
        lock.state = tmpLock.state
        lock = tmpLock
        lockLoading.value = false
        isAfterLock.value = {}
      }, (lockState.onSeconds + 1) * 1000)
    }
  } else {
    lockLoading.value = false
  }
}

const getLocationDoor = () => {
  if (navigator.geolocation) {
    watchPosId.value = navigator.geolocation.watchPosition(LocationSuccess, LocationError);
  } else {
    locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  }
}
const degreesToRadians = (degrees:number) => {
  return degrees * Math.PI / 180;
}

const LocationError = (error:any) => {
  locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  isAfterLock.value = {}
  lockLoading.value = false
}

const distanceInKmBetweenEarthCoordinates = (lat1:number, lon1:number, lat2:number, lon2:number) => {
  let earthRadiusKm = 6371;

  let dLat = degreesToRadians(lat2-lat1);
  let dLon = degreesToRadians(lon2-lon1);

  lat1 = degreesToRadians(lat1);
  lat2 = degreesToRadians(lat2);

  let a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
  let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return earthRadiusKm * c;
}

const LocationSuccess = (position:any) => {
  let lockOpen = false
  finalLocks.value.forEach((lock:any) => {
    let kms = distanceInKmBetweenEarthCoordinates(lock?.geoLocation?.lat, lock?.geoLocation?.lon, position.coords.latitude,position.coords.longitude)
    let meters = Math.floor(kms * 1000)
    if(meters <= lock.geoMaxDistance){
      lock.enabled = true
      lockOpen = true
    } else {
      lock.enabled = false
    }
  })
  if(!lockOpen){
    locationErrorMsq.value = 'Must be at venue to open doors on Device, if issues please use pin code on reader'
  }

  if(isAfterLock?.value?.id){
    locationErrorMsq.value = '';
    openDoor(isAfterLock.value)
    isAfterLock.value = {}
  }
}

onUnmounted(()=>{
  if (navigator.geolocation) {
    navigator.geolocation.clearWatch(watchPosId.value);
  }
  isAfterLock.value = {}
})

// Hooks
onMounted(async () => {
  isBookingStepperVisible.value = false;
  await getLocationList();
  await getListArea();
  await getPriorityBookings(userId.value);
  if (currentLocation.value) await fetchActivities();

});
</script>

<template>
  <v-dialog v-model="openViewOnlyWindow" persistant max-width="900">
    <VCard height="auto" flat>
      <div class="d-flex align-center justify-space-between bg-dialogHeaderColor">

        <!-- 👉 Header -->
        <VCardTitle>
          {{viewOnlyWindowBooking?.expand?.activity?.name}} Booking Details
        </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="openViewOnlyWindow = false"
        />
      </div>

      <VCardText v-if="openViewOnlyWindow" class="text-center">
        <VDivider />
        <VContainer>
          <p class="text-h6 mb-1">
            {{ globalSetup?.areaSingular || "Bay" }}
            {{ viewOnlyWindowBooking?.expand?.area?.areaNumber }}

            {{
              viewOnlyWindowBooking?.source === "Imported"
                ? " Imported From: " + viewOnlyWindowBooking.extSource
                : viewOnlyWindowBooking?.source
            }}
          </p>
          <div class="text-h6 mb-1">
            {{
              viewOnlyWindowBooking.referrer
                ? "Referrer: " + viewOnlyWindowBooking.referrer : ""
            }}
          </div>
          <div class="text-h6 mb-1">
            {{ FormatDate(viewOnlyWindowBooking.startTime) }} for
            {{ viewOnlyWindowBooking.duration - 5 }} Mins
          </div>
          <div class="text-h6 mb-1" v-if="viewOnlyWindowBooking?.members?.expand?.users.length">
            Members: {{ viewOnlyWindowBooking.members?.expand?.users.map((u:any) => u.firstName + ' ' + u.lastName).join(",") }}
          </div>
          <div class="text-h6 mb-1" v-if="viewOnlyWindowBooking?.visitors">
            Visitors: {{viewOnlyWindowBooking.visitors || "None"}}
          </div>

          <div class="text-h6 mb-1">
            Contact:
            <span v-if="viewOnlyWindowBooking.members?.expand?.users.length > 0">
            {{viewOnlyWindowBooking.members?.expand?.users.filter((u:any) => u.id === viewOnlyWindowBooking.owner)[0]?.email}}
            {{viewOnlyWindowBooking.members?.expand?.users.filter((u:any) => u.id === viewOnlyWindowBooking.owner)[0]?.mobileNumber}}
              </span>
            <span v-else>None given</span>
          </div>

          <VCol v-if="viewOnlyWindowBooking?.members?.expand?.options.length > 0" cols="12">
            <div>
              <div class="d-flex flex-wrap gap-4">
                <VCheckbox
                  v-for="product in viewOnlyWindowBooking?.members?.expand?.options"
                  :label="`${product.name}`"
                  :model-value="true"
                  readonly
                  color="primary"
                  density="compact"
                />
              </div>
            </div>
          </VCol>
        </VContainer>
        <VDivider />
      </VCardText>
      <VCardSubtitle
        v-if="viewOnlyWindowBooking?.status !== 'futureBooking'"
        class="pa-5 pb-0 font-weight-medium">
        Options limited as booking {{ viewOnlyWindowBooking.status === "completed" ? "completed" : "started" }}
      </VCardSubtitle>
    </VCard>
  </v-dialog>


  <VDialog v-if="viewQR"
    :model-value="viewQR"
    persistent
    class="d-flex justify-center align-center qr-dialog pair_terminal_dialog"
  >
    <VCard
      height="auto"
      width="400"
      class="rounded-lg shadow-md bg-gradient-to-br from-blue-50 to-blue-100"
      style="max-height:90vh;max-width:95vw">
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <VCardTitle>Scan for Access</VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="viewQR = false"
        ></VBtn>
      </div>
      <div class="px-6 py-4 text-center">
        <qrcode-vue
          v-if="viewOnlyWindowBooking?.entryCode && viewOnlyWindowBooking?.entryCode != ''"
          text="testing"
          :value="`BT#${viewOnlyWindowBooking?.entryCode ? viewOnlyWindowBooking?.entryCode : viewOnlyWindowBooking.id}`"
          level="M"
          render-as="svg"
          background="#ffffff"
          foreground="#000000"
          :gradient="false"
          gradient-type="linear"
          gradient-start-color="#000000"
          gradient-end-color="#38bdf8"
          :margin="2"
          size="320"
          class="qr-code_container"
          style="max-width:100%"
        />
        <div class="mt-4"></div>
        <h3 v-if="viewOnlyWindowBooking?.entryCode && viewOnlyWindowBooking?.entryCode != ''">Entry Code: {{viewOnlyWindowBooking?.entryCode }}</h3>
        <p class="font-weight-medium">
          valid for single booking
        </p>
        <v-alert color="error" variant="tonal" class="mb-3" v-if="locationErrorMsq != ''">
          {{locationErrorMsq}}
        </v-alert>
        <div v-for="lock in finalLocks" class="mb-2">
          <v-btn class="w-100" @click.prevent="openDoor(lock)" :loading="lockLoading" :color="lock.state=='Open' ? 'success' : 'primary'">Open {{lock.name}}</v-btn>
        </div>

      </div>
    </VCard>
  </VDialog>

  <VCard
    v-if="priorityBookings.length"
    class="bookings-container"
    :class="{
      dark_theme: $vuetify.theme.current.dark,
      light_theme: !$vuetify.theme.current.dark,
    }"
  >
    <VCardTitle class="title">Upcoming bookings</VCardTitle>

    <VRow v-if="isLoading">
      <VCol v-for="n in 2" :key="n" cols="12" md="6" lg="3">
        <div class="booking-skeleton">
          <VCard class="booking-card skeleton animated-shimmer">
            <VCardText>
              <div class="skeleton-header shimmer"></div>
              <div class="skeleton-chips">
                <div class="skeleton-chip shimmer"></div>
                <div class="skeleton-chip shimmer"></div>
              </div>
              <div class="skeleton-details">
                <div class="skeleton-detail shimmer"></div>
              </div>
            </VCardText>
          </VCard>
        </div>
      </VCol>
    </VRow>

    <VRow v-else>
      <VCol
        v-for="booking in priorityBookings"
        :key="booking.id"
        cols="12"
        md="6"
        lg="3"
      >
        <VCard
          class="booking-card pb-0"
          @click="handleEdit(booking)"
          :class="{ 'due-card': booking.due > 0 }"
        >
          <!-- Icon for recurring booking in top-right corner -->
          <VIcon
            v-if="
              !isEmpty(booking.recurringEventDetails) ||
              !isEmpty(booking.recurringBookingDetails)
            "
            class="top-right-icon recurring-icon"
            color="black"
          >
            mdi-calendar-outline
          </VIcon>

          <!-- Icon for event in top-left corner -->
          <VIcon
            v-if="!isEmpty(booking.eventDetails)"
            class="top-left-icon event-icon"
            color="black"
          >
            mdi-star-outline
          </VIcon>

          <VCardText class="card-content pt-2 d-flex justify-space-between">
            <div></div>
            <div class="text-center">
              <div class="booking-header d-flex justify-center align-center">
                <div class="activity" style="font-size:22px;font-weight:bold">
                  {{ moment(booking.startTime).format("h:mm A") }}
                </div>
              </div>
              <div class="booking-details d-flex justify-center align-center">
                <div class="mt-2">
                  <span class="info">
                    {{ getDay(booking.startTime) }}
                  </span>
                </div>
              </div>
              <div class="booking-details d-flex justify-center">
                <div class="mt-2">
                  <span class="info">
                    <VIcon icon="mdi-location" aria-label="Location" />
                    {{ getLocation(booking?.expand?.area) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="d-flex align-end align-center">
              <IconBtn
                aria-label="QR Code"
                @click.stop="openQR(booking)"
                color="black">
                <VIcon size="40" icon="mdi-qrcode" aria-label="QR Icon"/>
              </IconBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Booking dialog -->
    <BookingStepper
      v-model:isDrawerOpen="isBookingStepperVisible"
      :booking="editBookingData"
      :recurring-details="recurringDetails"
      :activity-list="activityList"
      :area-list="listArea"
      :memberList="usersList"
      :location-list="locationList"
      :current-location="currentLocation"
      :current-logged-in-roles="getMyRole"
      :membership="userInfo?.expand?.membership"
      :user-id="userId"
      :permission="permissionListMap?.bookings ?? []"
      :coupon-value="globalSetup?.couponValue"
      @loadUsersList="getUserList()"
      @update:isDrawerOpen="closeDialog"
      @delete-booking="deleteBooking($event)"
      @addNewCardDialog=""
      @closeDialog="
        ((editBookingData = {}),
        (recurringDetails = {}),
        (isBookingStepperVisible = $event))
      "
      @reloadBookingList="getPriorityBookings(userId)"
    />

    <!-- Event dialog -->
    <AddEditEvents
      :is-drawer-open="addEditEventDialog"
      :event="eventData"
      :recurring-details="eventRecurringDetails"
      :activity-list="activityList"
      :area-list="listArea"
      :memberList="usersList"
      :location-list="locationList"
      :current-location="currentLocation"
      :area-plural="globalSetup?.areaPlural || 'Bays'"
      :area-singular="globalSetup?.areaSingular || 'Bay'"
      :current-logged-in-roles="getMyRole"
      :membership="userInfo?.expand?.membership"
      :user-id="userId"
      :permission="permissionListMap?.bookings"
      :coupon-value="globalSetup?.couponValue"
      @closeDialog="addEditEventDialog = false"
    />

    <!-- Suggestion edit dialog -->
    <EditConfirmBookingDialog
      :is-visible="isConfirmEditBookingDialog"
      :title="confirmEditDialog?.title"
      :alertMessage="confirmEditDialog?.alertMessage"
      @confirmEdit="
        ($event) => {
          isConfirmEditBookingDialog = false;
          if (confirmEditDialog?.type === 'Event') {
            eventRecurringDetails = {
              ...eventRecurringDetails,
              isEditAll: $event === 'ALL' ? true : false,
            };
            handleEventEdit(selectedEvent);
          } else {
            handleBookingEdit($event);
          }
        }
      "
      @closeDialog="
        ((editBookingData = {}),
        (eventRecurringDetails = {}),
        (isConfirmEditBookingDialog = $event))
      "
    />

    <!-- QR code Dialog -->
    <QrCode
      :is-visible="isQrCodeVisible"
      :qr-value="selectedBookingId"
      @closeDialog="isQrCodeVisible = false"
    />
  </VCard>
</template>

<style scoped lang="scss">
.bookings-container {
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 10%);
  color: var(--text-color);
  cursor: pointer;
}

.title {
  font-size: 23px;
  font-weight: 600;
  margin-block-end: 10px;
  text-align: center;
}

.loading-row {
  display: flex;
  align-items: center;
  justify-content: center;
  block-size: 100px;
}

.animated-shimmer {
  position: relative;
  overflow: hidden;
  background: var(--skeleton-bg);
}

.shimmer {
  animation: shimmer 1.5s infinite;
  background: var(--shimmer-bg);
  background-size: 200% 100%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.booking-skeleton {
  padding: 8px;
  border-radius: 10px;
}

.skeleton-header {
  border-radius: 4px;
  background-color: var(--skeleton-bg);
  block-size: 15px;
  margin-block-end: 6px;
}

.skeleton-chips {
  display: flex;
  gap: 6px;
  margin-block-end: 6px;
}

.skeleton-chip {
  flex: 1;
  border-radius: 16px;
  background-color: var(--skeleton-bg);
  block-size: 10px;
}

.skeleton-details {
  line-height: 1.5;
}

.skeleton-detail {
  border-radius: 4px;
  background-color: var(--skeleton-bg);
  block-size: 15px;
  margin-block-end: 3px;
}

// Other styles remain unchanged

.booking-card {
  position: relative;
  overflow: hidden;
  padding: 10px;
  border-radius: 10px;
  background-color: rgb(var(--v-theme-dialogHeaderColor));
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    background-color 0.3s ease;
}

.booking-card:hover {
  background-color: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 15%);
  transform: scale(1.05);
}

.due-card {
  background-color: rgb(var(--v-theme-error)) !important;
}

.top-right-icon {
  position: absolute;
  inset-block-start: 10px;
  inset-inline-end: 10px;
}

.top-left-icon {
  position: absolute;
  inset-block-start: 10px;
  inset-inline-start: 10px;
}

.recurring-icon {
  font-size: 24px;
}

.event-icon {
  font-size: 24px;
}

.card-content {
  font-size: 19px;
}
</style>
